import { createContext, useCallback, useState, ReactNode } from 'react';

import { EntityType } from 'enums';
import {
  EnvironmentWithIndicators,
  IndicatorChart,
  PaginatedUserBrandEntity,
  PaginatedUserSchoolEntity,
  UserBrandEntity,
  UserSchoolEntity,
  ChartTypeEnum,
  IndicatorEvent,
} from 'generated';
import { useQuery } from 'hooks';

export const aboutContext: EnvironmentWithIndicators = {
  id: 'about',
  name: 'Sobre',
  order: 0,
  indicators: [
    {
      id: 'about',
      name: 'sobre',
      chartType: {
        GENERAL: ChartTypeEnum.Distribution,
      },
      description: '',
      filters: {},
      order: 0,
      scale: 0,
    },
  ],
};

export interface BootstrapContextData {
  entity?: UserBrandEntity | UserSchoolEntity;
  similarSchoolsCompare?: boolean;
  hasCompanyPermission: boolean;
  brands?: PaginatedUserBrandEntity;
  schools?: PaginatedUserSchoolEntity;
  entityType: EntityType;
  indicatorEventId: string;
  currentContext?: EnvironmentWithIndicators;
  currentIndicator?: IndicatorChart;
  globalLoading?: boolean;
  contexts: EnvironmentWithIndicators[];
  indicatorEvent?: IndicatorEvent;
  updateGlobalLoading(value: boolean): void;
  updateIndicatorEvent(event: IndicatorEvent): void;
  updateSimiliarSchoolsCompare(value: boolean): void;
  updateHasCompanyPermission(value: boolean): void;
  updateBrands(newBrands?: PaginatedUserBrandEntity): void;
  updateSchools(newSchools?: PaginatedUserSchoolEntity): void;
  updateIndicator(contextId: string, indicatorId: string): void;
  updateContexts(newContexts: EnvironmentWithIndicators[]): void;
  updateCurrentContext(context: EnvironmentWithIndicators): void;
  updateCurrentIndicator(indicator: IndicatorChart): void;
  updateEntity(
    newEntity: UserBrandEntity | UserSchoolEntity,
    type: EntityType,
  ): void;
}

interface BootstrapProviderProps {
  children: ReactNode;
}

export const BootstrapContext = createContext<BootstrapContextData>(
  {} as BootstrapContextData,
);

export function BootstrapProvider({
  children,
}: BootstrapProviderProps): JSX.Element {
  const query = useQuery();

  const [globalLoading, setGlobalLoading] = useState<boolean>(true);
  const [indicatorEvent, setIndicatorEvent] = useState<
    IndicatorEvent | undefined
  >();
  const [similarSchoolsCompare, setSimilarSchoolsCompare] =
    useState<boolean>(false);
  const [hasCompanyPermission, setHasCompanyPermission] =
    useState<boolean>(false);
  const [entityType, setEntityType] = useState<EntityType>(() => {
    const queryEntityType = query.get('entityType');

    if (queryEntityType) {
      return queryEntityType as EntityType;
    }

    return EntityType.SCHOOL;
  });

  const [entity, setEntity] = useState<
    UserBrandEntity | UserSchoolEntity | undefined
  >();
  const [brands, setBrands] = useState<PaginatedUserBrandEntity | undefined>();
  const [schools, setSchools] = useState<
    PaginatedUserSchoolEntity | undefined
  >();
  const [contexts, setContexts] = useState<EnvironmentWithIndicators[]>([]);
  const [currentContext, setCurrentContext] = useState<
    EnvironmentWithIndicators | undefined
  >();
  const [currentIndicator, setCurrentIndicator] = useState<
    IndicatorChart | undefined
  >();
  const [indicatorEventId] = useState<string>(() => {
    const queryIndicatorEventId = query.get('indicatorEventId');

    if (queryIndicatorEventId) {
      return queryIndicatorEventId;
    }

    return '8666e29a-d589-483e-b2b4-1b23ce97cadf';
  });

  const updateIndicator = useCallback(
    (contextId: string, indicatorId: string) => {
      const context = contexts.find(context => context.id === contextId);

      if (!context) {
        return;
      }

      const indicator = context.indicators.find(
        indicator => indicator.id === indicatorId,
      );

      if (!indicator) {
        return;
      }

      setCurrentContext(context);
      setCurrentIndicator(indicator);
    },
    [contexts],
  );

  const updateEntity = useCallback(
    (newEntity: UserBrandEntity | UserSchoolEntity, type: EntityType) => {
      setEntityType(type);
      setEntity(newEntity);
    },
    [],
  );

  const updateContexts = useCallback(
    (newContexts: EnvironmentWithIndicators[]) => {
      setContexts([aboutContext, ...newContexts]);
    },
    [],
  );

  return (
    <BootstrapContext.Provider
      value={{
        entity,
        brands,
        schools,
        contexts,
        entityType,
        currentIndicator,
        currentContext,
        indicatorEventId,
        indicatorEvent,
        updateIndicator,
        updateContexts,
        updateEntity,
        globalLoading,
        similarSchoolsCompare,
        hasCompanyPermission,
        updateBrands: setBrands,
        updateSchools: setSchools,
        updateGlobalLoading: setGlobalLoading,
        updateIndicatorEvent: setIndicatorEvent,
        updateCurrentContext: setCurrentContext,
        updateCurrentIndicator: setCurrentIndicator,
        updateHasCompanyPermission: setHasCompanyPermission,
        updateSimiliarSchoolsCompare: setSimilarSchoolsCompare,
      }}
    >
      {children}
    </BootstrapContext.Provider>
  );
}
